import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import "../../css/HomePage.css";
import "./NotebookList.css";
import NotebookExportDialog from "./NotebookExportDialog";

function NotebookList({ onNotebookSelect, onCreateNotebook, onNotebookUpdate, onBack }) {
    const [notebooks, setNotebooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uid, setUid] = useState("");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showRenameModal, setShowRenameModal] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [renamePageMode, setRenamePageMode] = useState('scaling');
    const [showPalmTipsModal, setShowPalmTipsModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [exportingNotebook, setExportingNotebook] = useState(null);
    const [newNotebookData, setNewNotebookData] = useState({
        title: 'New Notebook',
        description: '',
        page_mode: 'scaling',
        background_type: 'white',
        cover_color: '#4A90E2',
        initial_width: 1200,
        initial_height: 800
    });
    const navigate = useNavigate();

    const handleBackToHome = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/');
        }
    };

    const handleSettingsClick = () => { navigate('/my-notebook/settings') }

    const openPalmTipsModal = () => setShowPalmTipsModal(true);
    const closePalmTipsModal = () => setShowPalmTipsModal(false);

    const getUserPlatform = () => {
        const platform = navigator.platform.toLowerCase();
        const userAgent = navigator.userAgent.toLowerCase();
        
        if (platform.includes('win') || userAgent.includes('windows')) {
            return 'windows';
        } else if (platform.includes('mac') || userAgent.includes('macintosh')) {
            return 'macos';
        } else if (userAgent.includes('ipad') || (userAgent.includes('macintosh') && 'ontouchend' in document)) {
            return 'ipad';
        } else if (userAgent.includes('android')) {
            return 'android';
        } else if (userAgent.includes('linux')) {
            return 'linux';
        }
        return 'unknown';
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
                loadNotebooks(user.uid);
            } else {
                setUid("");
                setNotebooks([]);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const loadNotebooks = async (userId) => {
        setLoading(true);
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                console.error("User is not authenticated.");
                setNotebooks([]);
                setLoading(false);
                return;
            }

            const response = await fetch(
                "https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/",
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": userId,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const notebooksWithFormattedData = data.notebooks.map(notebook => ({
                id: notebook.id,
                title: notebook.title,
                description: notebook.description,
                coverColor: notebook.cover_color,
                backgroundType: notebook.background_type,
                pageMode: notebook.page_mode,
                pageCount: notebook.page_count,
                isPublic: notebook.is_public,
                createdAt: notebook.created_at,
                updatedAt: notebook.updated_at
            }));
            
            setNotebooks(notebooksWithFormattedData);
        } catch (error) {
            console.error('Error loading notebooks:', error);
            setNotebooks([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNotebook = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                console.error("User is not authenticated.");
                return;
            }

            const response = await fetch(
                "https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": uid,
                    },
                    body: JSON.stringify({
                        title: newNotebookData.title,
                        description: newNotebookData.description,
                        cover_color: newNotebookData.cover_color,
                        background_type: newNotebookData.background_type,
                        page_mode: newNotebookData.page_mode,
                        initial_width: newNotebookData.initial_width,
                        initial_height: newNotebookData.initial_height,
                        is_public: false
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const newNotebookApiData = await response.json();
            const newNotebook = {
                id: newNotebookApiData.id,
                title: newNotebookApiData.title,
                description: newNotebookApiData.description,
                coverColor: newNotebookApiData.cover_color,
                backgroundType: newNotebookApiData.background_type,
                pageMode: newNotebookApiData.page_mode,
                pageCount: newNotebookApiData.page_count,
                isPublic: newNotebookApiData.is_public,
                createdAt: newNotebookApiData.created_at,
                updatedAt: newNotebookApiData.updated_at
            };

            setNotebooks(prev => [...prev, newNotebook]);
            setShowCreateModal(false);
            
            setNewNotebookData({
                title: 'New Notebook',
                description: '',
                page_mode: 'scaling',
                background_type: 'white',
                cover_color: '#4A90E2',
                initial_width: 1200,
                initial_height: 800
            });
            
            if (onCreateNotebook) {
                onCreateNotebook(newNotebook);
            }
        } catch (error) {
            console.error('Error creating notebook:', error);
            alert('Failed to create notebook. Please try again.');
        }
    };

    const handleDeleteNotebook = async (notebookId, notebookTitle) => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                console.error("User is not authenticated.");
                return;
            }

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebookId}/`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": uid,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            setNotebooks(prev => prev.filter(notebook => notebook.id !== notebookId));
            setShowDeleteConfirm(null);
            
        } catch (error) {
            console.error('Error deleting notebook:', error);
            alert('Failed to delete notebook. Please try again.');
        }
    };

    const handleRenameNotebook = async (notebookId, newTitle, newPageMode = null) => {
        if (!newTitle.trim()) {
            alert('Notebook title cannot be empty.');
            return;
        }

        try {
            const user = auth.currentUser;
            
            if (!user) {
                console.error("User is not authenticated.");
                return;
            }
            
            const token = await user.getIdToken();
            if (!token) {
                console.error("Failed to get authentication token.");
                return;
            }

            const updateData = { title: newTitle.trim() };
            if (newPageMode !== null) {
                updateData.page_mode = newPageMode;
            }
            if (showRenameModal?.coverColor) {
                updateData.cover_color = showRenameModal.coverColor;
            }

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebookId}/`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": uid,
                    },
                    body: JSON.stringify(updateData),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const updatedNotebookData = await response.json();
            
            setNotebooks(prev => prev.map(notebook => 
                notebook.id === notebookId 
                    ? { 
                        ...notebook, 
                        title: updatedNotebookData.title, 
                        pageMode: updatedNotebookData.page_mode,
                        coverColor: updatedNotebookData.cover_color,
                        updatedAt: updatedNotebookData.updated_at 
                    }
                    : notebook
            ));
            
            if (onNotebookUpdate) {
                onNotebookUpdate({
                    id: notebookId,
                    title: updatedNotebookData.title,
                    pageMode: updatedNotebookData.page_mode,
                    updatedAt: updatedNotebookData.updated_at
                });
            }
            
            setShowRenameModal(null);
            setRenameValue("");
            setRenamePageMode('scaling');
            
        } catch (error) {
            console.error('Error updating notebook:', error);
            alert('Failed to update notebook. Please try again.');
        }
    };

    const confirmDelete = (notebook, event) => {
        event.stopPropagation();
        setShowDeleteConfirm(notebook);
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(null);
    };

    const startRename = (notebook, event) => {
        event.stopPropagation();
        setShowRenameModal(notebook);
        setRenameValue(notebook.title);
        setRenamePageMode(notebook.pageMode || 'scaling');
    };

    const cancelRename = () => {
        setShowRenameModal(null);
        setRenameValue("");
        setRenamePageMode('scaling');
    };

    const handleRenameSubmit = (e) => {
        e.preventDefault();
        if (showRenameModal) {
            handleRenameNotebook(showRenameModal.id, renameValue, renamePageMode);
        }
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffTime = now - date;
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays === 0) {
                return 'today';
            } else if (diffDays === 1) {
                return 'yesterday';
            } else if (diffDays < 7) {
                return `${diffDays} days ago`;
            } else if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
            } else {
                return date.toLocaleDateString();
            }
        } catch (error) {
            return 'Unknown';
        }
    };

    if (loading) {
        return (
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: 'var(--bb-accent)', fontSize: '1.1rem', marginTop: 16 }}>Loading notebooks...</p>
            </div>
        );
    }

    return (
        <div className="NotebookList__container">
            <div className="NotebookList__header">
                <button className="NotebookList__back-btn" onClick={handleBackToHome}>
                    <i className="fas fa-arrow-left"></i>
                </button>
                <h1 className="NotebookList__title">My Notebooks</h1>
                <div className="NotebookList__header-actions">
                    <button 
                        className="NotebookList__tips-btn"
                        onClick={openPalmTipsModal}
                        title="Palm rejection tips"
                    >
                        <i className="fas fa-hand-paper"></i>
                    </button>
                    <button 
                        className="NotebookList__settings-btn"
                        onClick={handleSettingsClick}
                        title="Notebook Settings"
                    >
                        <i className="fas fa-cog"></i>
                    </button>
                </div>
            </div>
            
            <div className="NotebookList__grid">
                {/* Create New Notebook Card */}
                <div 
                    className="NotebookList__card NotebookList__card--create-new"
                    onClick={() => setShowCreateModal(true)}
                >
                    <div className="NotebookList__create-new-icon">
                        <i className="fas fa-plus"></i>
                    </div>
                    <span className="NotebookList__create-new-text">New Notebook</span>
                </div>

                {/* Existing Notebooks */}
                {notebooks.map(notebook => (
                    <div 
                        key={notebook.id}
                        className="NotebookList__card"
                        onClick={() => onNotebookSelect(notebook)}
                        style={{ borderColor: notebook.coverColor || '#4A90E2' }}
                    >
                        <div 
                            className="NotebookList__card-cover"
                            style={{ backgroundColor: notebook.coverColor || '#4A90E2' }}
                        >
                            <div className="NotebookList__card-lines"></div>
                        </div>
                        <div className="NotebookList__card-info">
                            <h3 className="NotebookList__card-title">{notebook.title}</h3>
                            <div className="NotebookList__card-meta">
                                <span className="NotebookList__card-pages">
                                    <i className="fas fa-file"></i> {notebook.pageCount || 1} page{notebook.pageCount !== 1 ? 's' : ''}
                                </span>
                                <span className="NotebookList__card-updated">
                                    Edited {formatDate(notebook.updatedAt)}
                                </span>
                            </div>
                        </div>
                        <div className="NotebookList__card-actions">
                            <button 
                                className="NotebookList__card-action-btn NotebookList__card-action-btn--edit"
                                onClick={(e) => startRename(notebook, e)}
                                title="Edit notebook"
                            >
                                <i className="fas fa-edit"></i>
                            </button>
                            <button 
                                className="NotebookList__card-action-btn NotebookList__card-action-btn--export"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExportingNotebook(notebook);
                                }}
                                title="Export to PDF"
                            >
                                <i className="fas fa-file-pdf"></i>
                            </button>
                            <button 
                                className="NotebookList__card-action-btn NotebookList__card-action-btn--delete"
                                onClick={(e) => confirmDelete(notebook, e)}
                                title="Delete notebook"
                            >
                                <i className="fas fa-trash-alt"></i>
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {exportingNotebook && (
                <NotebookExportDialog
                    notebookId={exportingNotebook.id}
                    title={exportingNotebook.title}
                    pageCount={exportingNotebook.pageCount}
                    onClose={() => setExportingNotebook(null)}
                />
            )}

            {/* Create Notebook Modal */}
            {showCreateModal && (
                <div className="NotebookList__modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="NotebookList__create-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="NotebookList__modal-header">
                            <h2>Create New Notebook</h2>
                            <button className="NotebookList__modal-close" onClick={() => setShowCreateModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="NotebookList__modal-body">
                            <div className="NotebookList__form-group">
                                <label>Title</label>
                                <input
                                    type="text"
                                    value={newNotebookData.title}
                                    onChange={(e) => setNewNotebookData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Notebook title"
                                />
                            </div>
                            <div className="NotebookList__form-group">
                                <label>Description (optional)</label>
                                <textarea
                                    value={newNotebookData.description}
                                    onChange={(e) => setNewNotebookData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Brief description"
                                    rows={3}
                                />
                            </div>
                            <div className="NotebookList__form-group">
                                <label>Cover Color</label>
                                <div className="NotebookList__color-picker">
                                    {['#4A90E2', '#E24A4A', '#4AE24A', '#E2E24A', '#E24AE2', '#4AE2E2', '#888888', '#333333'].map(color => (
                                        <button
                                            key={color}
                                            className={`NotebookList__color-option ${newNotebookData.cover_color === color ? 'NotebookList__color-option--selected' : ''}`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => setNewNotebookData(prev => ({ ...prev, cover_color: color }))}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="NotebookList__form-group">
                                <label>Page Mode</label>
                                <select
                                    value={newNotebookData.page_mode}
                                    onChange={(e) => setNewNotebookData(prev => ({ ...prev, page_mode: e.target.value }))}
                                >
                                    <option value="scaling">Scaling (fits screen)</option>
                                    <option value="infinite">Infinite (scrollable)</option>
                                </select>
                            </div>
                            <div className="NotebookList__form-group">
                                <label>Background</label>
                                <select
                                    value={newNotebookData.background_type}
                                    onChange={(e) => setNewNotebookData(prev => ({ ...prev, background_type: e.target.value }))}
                                >
                                    <option value="white">White</option>
                                    <option value="grid">Grid</option>
                                    <option value="lined">Lined</option>
                                    <option value="dotted">Dotted</option>
                                </select>
                            </div>
                        </div>
                        <div className="NotebookList__modal-footer">
                            <button className="NotebookList__btn NotebookList__btn--secondary" onClick={() => setShowCreateModal(false)}>
                                Cancel
                            </button>
                            <button className="NotebookList__btn NotebookList__btn--primary" onClick={handleCreateNotebook}>
                                Create Notebook
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="NotebookList__modal-overlay" onClick={cancelDelete}>
                    <div className="NotebookList__delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="NotebookList__modal-header">
                            <h2>Delete Notebook?</h2>
                        </div>
                        <div className="NotebookList__modal-body">
                            <p>Are you sure you want to delete "{showDeleteConfirm.title}"? This action cannot be undone.</p>
                        </div>
                        <div className="NotebookList__modal-footer">
                            <button className="NotebookList__btn NotebookList__btn--secondary" onClick={cancelDelete}>
                                Cancel
                            </button>
                            <button 
                                className="NotebookList__btn NotebookList__btn--danger" 
                                onClick={() => handleDeleteNotebook(showDeleteConfirm.id, showDeleteConfirm.title)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Modal */}
            {showRenameModal && (
                <div className="NotebookList__modal-overlay" onClick={cancelRename}>
                    <div className="NotebookList__rename-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="NotebookList__modal-header">
                            <h2>Edit Notebook</h2>
                            <button className="NotebookList__modal-close" onClick={cancelRename}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleRenameSubmit}>
                            <div className="NotebookList__modal-body">
                                <div className="NotebookList__form-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        placeholder="Notebook title"
                                        autoFocus
                                    />
                                </div>
                                <div className="NotebookList__form-group">
                                    <label>Page Mode</label>
                                    <select
                                        value={renamePageMode}
                                        onChange={(e) => setRenamePageMode(e.target.value)}
                                    >
                                        <option value="scaling">Scaling (fits screen)</option>
                                        <option value="infinite">Infinite (scrollable)</option>
                                    </select>
                                </div>
                                <div className="NotebookList__form-group">
                                    <label>Cover Color</label>
                                    <div className="NotebookList__color-picker">
                                        {['#4A90E2', '#E24A4A', '#4AE24A', '#E2E24A', '#E24AE2', '#4AE2E2', '#888888', '#333333'].map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`NotebookList__color-option ${(showRenameModal.coverColor || '#4A90E2') === color ? 'NotebookList__color-option--selected' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => setShowRenameModal(prev => ({ ...prev, coverColor: color }))}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="NotebookList__modal-footer">
                                <button type="button" className="NotebookList__btn NotebookList__btn--secondary" onClick={cancelRename}>
                                    Cancel
                                </button>
                                <button type="submit" className="NotebookList__btn NotebookList__btn--primary">
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Palm Tips Modal */}
            {showPalmTipsModal && (
                <div className="NotebookList__modal-overlay" onClick={closePalmTipsModal}>
                    <div className="NotebookList__palm-tips-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="NotebookList__modal-header">
                            <h2>✋ Palm Rejection Tips</h2>
                            <button className="NotebookList__modal-close" onClick={closePalmTipsModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="NotebookList__modal-body">
                            <p>Palm rejection helps prevent accidental marks when resting your hand on the screen while drawing.</p>
                            
                            <div className="NotebookList__palm-tip-section">
                                <h3>Tips for {getUserPlatform() === 'ipad' ? 'iPad' : getUserPlatform() === 'android' ? 'Android' : 'Your Device'}</h3>
                                <ul>
                                    <li>Use a stylus for best results</li>
                                    <li>Rest your palm lightly on the screen</li>
                                    <li>Bluebird automatically detects and ignores palm touches</li>
                                    <li>Adjust sensitivity in Settings if needed</li>
                                </ul>
                            </div>
                            
                            <div className="NotebookList__palm-tip-section">
                                <h3>How It Works</h3>
                                <p>Bluebird uses advanced touch detection to differentiate between intentional stylus/finger input and accidental palm contact.</p>
                            </div>
                        </div>
                        <div className="NotebookList__modal-footer">
                            <button className="NotebookList__btn NotebookList__btn--primary" onClick={closePalmTipsModal}>
                                Got It!
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotebookList;
